// ThemeToggle — flips light/dark and persists the choice (lib/theme.ts).
// `useTheme` is exported for anything else that needs to read/set the theme.
//
// Colors are explicit white-alpha rather than `currentColor`. It used to
// inherit, which worked only by accident: the surrounding bar's text color
// came from `var(--sf)`, a surface token standing in for white. That token
// is white in light mode but #161b26 in dark, so the icon inherited the
// bar's own background color and the button rendered visibly empty. The bar
// is a dark ground in BOTH themes (brand blue / --sf), so white-alpha is
// correct in both and matches the Help button beside it.

import { useEffect, useState } from 'react';
import Icon from './Icon';
import { currentTheme, toggleTheme, Theme } from '../../lib/theme';

/** React binding over lib/theme — re-renders on theme changes. */
export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setThemeState] = useState<Theme>(() => currentTheme());
  useEffect(() => {
    const onChange = () => setThemeState(currentTheme());
    window.addEventListener('fnsai:themechange', onChange as EventListener);
    return () => window.removeEventListener('fnsai:themechange', onChange as EventListener);
  }, []);
  return { theme, toggle: () => toggleTheme() };
}

export interface ThemeToggleProps {
  className?: string;
  style?: React.CSSProperties;
}

export default function ThemeToggle({ className, style }: ThemeToggleProps) {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggle}
      className={
        'inline-flex cursor-pointer items-center justify-center rounded-lg ' +
        'border border-white/25 bg-transparent p-[7px] leading-none ' +
        'text-white/90 transition-colors hover:bg-white/10 ' +
        (className ?? '')
      }
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-pressed={isDark}
      style={style}
    >
      <Icon name={isDark ? 'sun' : 'moon'} size={16} />
    </button>
  );
}
